import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getActiveTheme, TOKEN_KEYS, type ThemePreset } from "@/lib/theme.functions";
import { BODY_FONTS, HEADING_FONTS, findFont, googleFontsUrl } from "@/lib/theme-fonts";

const FONT_LINK_ID = "theme-google-fonts";
const STYLE_TAG_ID = "theme-vars";

const TOKEN_TO_VAR: Record<string, string> = {
  background: "--background",
  foreground: "--foreground",
  muted: "--muted",
  muted_foreground: "--muted-foreground",
  accent: "--accent",
  accent_foreground: "--accent-foreground",
  gold: "--gold",
  forest: "--forest",
  border: "--border",
};

const FALLBACK_TOKENS: Record<string, string> = {
  background: "#0a0907",
  foreground: "#fbfaf7",
  muted: "#2a251f",
  muted_foreground: "#bab1a4",
  accent: "#d6a45e",
  accent_foreground: "#15110d",
  gold: "#dfae65",
  forest: "#2b4a37",
  border: "rgba(255,255,255,0.12)",
};

function safeTokenValue(key: string, value: string) {
  if (typeof CSS === "undefined" || CSS.supports("color", "oklch(1 0 0)")) return value;
  return value.trim().startsWith("oklch(") ? FALLBACK_TOKENS[key] : value;
}

function applyTheme(theme: ThemePreset) {
  if (typeof document === "undefined") return;

  // Inject color tokens as CSS variables on :root via a managed style tag.
  let style = document.getElementById(STYLE_TAG_ID) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement("style");
    style.id = STYLE_TAG_ID;
    document.head.appendChild(style);
  }
  const decls = TOKEN_KEYS.map((k) => {
    const v = theme.tokens[k];
    if (!v) return "";
    return `${TOKEN_TO_VAR[k]}:${safeTokenValue(k, v)};`;
  }).join("");
  style.textContent = `:root{${decls}--font-serif:'${theme.heading_font}', ui-serif, Georgia, serif;--font-sans:'${theme.body_font}', ui-sans-serif, system-ui, sans-serif;}`;

  // Swap the Google Fonts <link> for the chosen pair.
  const heading = findFont(HEADING_FONTS, theme.heading_font);
  const body = findFont(BODY_FONTS, theme.body_font);
  const href = googleFontsUrl(heading, body);
  let link = document.getElementById(FONT_LINK_ID) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.id = FONT_LINK_ID;
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  if (link.href !== href) link.href = href;
}

export default function ThemeApplier() {
  const fetchTheme = useServerFn(getActiveTheme);
  const { data } = useQuery({
    queryKey: ["active-theme"],
    queryFn: () => fetchTheme(),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (data) applyTheme(data);
  }, [data]);

  return null;
}
